import type { CachSapSoSanh, SoSanhKenh as SoSanhKenhDto } from '@qlk/shared';
import { CACH_SAP_NHAN, CACH_SAP_SO_SANH } from '@qlk/shared';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { EmptyState, SegmentedControl, Skeleton, soNguyen } from '../../../../ui/index.js';
import { dinhDangChiSo, nhanChenh } from './dinhDangChiSo.js';

/**
 * Khung "So sánh kênh · <chỉ số>".
 *
 * Ba chế độ: Tăng mạnh nhất (mặc định), Giảm mạnh nhất, Cao nhất. **Mỗi kênh
 * một dòng**, năm cột cố định nên các cột thẳng hàng dọc suốt bảng: hạng · tên
 * kênh với Network ngay dưới · thanh ngang · giá trị · chip mức đổi.
 *
 * ## Bảng liệt kê **mọi** kênh trong phạm vi
 *
 * Trước 31/10/2026 kênh không xếp hạng được bị **ẩn hẳn**, nên bảng của một kỳ
 * đang chạy chỉ còn 3 trong 16 kênh và không màn nào nói 13 kênh kia đi đâu.
 * Nay chúng xuống cuối, dưới vạch ngăn "Chưa đủ điều kiện so sánh", vẫn hiện
 * giá trị kỳ này **và** phần trăm — chip **xám** kèm lý do ngắn, nghĩa "con số
 * này để tham khảo".
 *
 * ## Chip phần trăm có ở **cả ba** chế độ
 *
 * `tang` và `giam` là cùng một bảng đọc ngược, nên một kênh **giảm** ở chế độ
 * "Tăng mạnh nhất" vẫn ở nhóm đủ điều kiện, nằm cuối nhóm, mang chip ▼ đỏ. Bản
 * 31/10/2026 đẩy nó sang nhóm "chưa đủ điều kiện" với lý do "không tăng trong
 * kỳ" — tức dán nhãn "không so được" lên một con số so được, và để nó lẫn với
 * kênh thiếu số liệu.
 *
 * Chế độ "Cao nhất" xếp **chỉ** theo giá trị kỳ này, nhưng vẫn in chip để người
 * đọc biết con số đó đang đi lên hay đi xuống.
 *
 * Lý do do máy chủ gửi (`lyDo`), không suy ở đây: giao diện tự suy thì nó có
 * thể nói khác luật mà máy chủ vừa áp.
 *
 * Lưới nằm trên **`<ol>`**, mỗi `<li>` mượn lại bằng `grid-cols-subgrid` — chứ
 * không phải mỗi dòng một lưới riêng. Cùng một `grid-template-columns` áp cho
 * từng dòng độc lập thì cột `auto` co theo nội dung **của riêng dòng đó**, nên
 * dòng có chip `▲ 4,2%` và dòng có chip `▲ 12,3%` lệch nhau vài px và cả bảng
 * so le (bài e2e 19 bắt đúng chỗ này: `1105, 1105, …, 1112`).
 *
 * Và không `flex`, cùng lý do: với flex thì cột giá trị của mỗi dòng bắt đầu ở
 * một chỗ khác nhau theo độ dài tên kênh, mắt không so được hai con số so le.
 *
 * Thanh ngang lấy tỷ lệ theo **giá trị lớn nhất đang hiện**, không theo tổng:
 * ở chế độ Giảm mạnh nhất tổng không có nghĩa gì, còn so với kênh đứng đầu thì
 * đọc được ở cả ba chế độ.
 *
 * Bấm tên kênh sang trang kênh và **giữ `?ky=`**: người ta bấm vào vì con số
 * của kỳ đó, mở ra một kỳ khác là mất mạch.
 */

/**
 * Năm cột của **cả bảng**, khai một lần trên `<ol>`.
 *
 * Cột tên rộng **tối thiểu 180px** từ `sm` trở lên và nội dung được **xuống
 * dòng**: tên kênh cộng tên Network là hai chuỗi dài, cắt bằng `…` thì hai kênh
 * cùng tiền tố đọc ra y như nhau ("WM Media Mu…" và "WM Media Re…").
 *
 * Dưới `sm` cột tên trở lại `minmax(0,1fr)`: ở 390px bề ngang, một sàn 180px
 * cộng cột giá trị và cột chip là tràn ngang — và tràn ngang thì cả trang phải
 * cuộn ngang, tệ hơn một cái tên xuống ba dòng.
 */
const LUOI_BANG =
  'grid grid-cols-[28px_minmax(0,1fr)_minmax(0,0.8fr)_auto_auto] sm:grid-cols-[28px_minmax(180px,1.4fr)_minmax(0,1fr)_auto_auto] gap-x-3 gap-y-4';

/** Mỗi dòng mượn lại đúng năm cột đó, không tự dựng lưới riêng. */
const LUOI_DONG = 'col-span-5 grid grid-cols-subgrid items-center';

export interface SoSanhKenhProps {
  du: SoSanhKenhDto | undefined;
  nhanChiSo: string;
  dangTai: boolean;
  loi: string | null;
  sap: CachSapSoSanh;
  onDoiSap: (sap: CachSapSoSanh) => void;
  /** Mã kỳ để gắn vào link sang trang kênh. */
  maKy: string;
  xemTatCa: boolean;
  onXemTatCa: () => void;
}

export function SoSanhKenh({
  du,
  nhanChiSo,
  dangTai,
  loi,
  sap,
  onDoiSap,
  maKy,
  xemTatCa,
  onXemTatCa,
}: SoSanhKenhProps) {
  const items = du?.items ?? [];
  const lonNhat = Math.max(...items.map((d) => d.giaTri ?? 0), 1);
  const conLai = (du?.tong ?? 0) - items.length;
  const soTheoDiem = du?.soTheoDiem ?? false;

  return (
    <section
      data-khoi="so-sanh-kenh"
      className="flex flex-col rounded-the border border-vien bg-be-mat p-6"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-tieu-de-khoi font-semibold text-chu">
            So sánh kênh · {nhanChiSo}
            {/*
              Khoảng kỳ trước ghi **một lần** ở đây, không nhắc lại trong từng
              dòng: 16 dòng cùng một khoảng là 16 lần cùng một con số, và nó đẩy
              cột giá trị rộng gấp đôi.
            */}
            {nhanKhoangChung(du) !== null && (
              <span data-o="khoang-ky-truoc" className="ml-2 text-bang font-normal text-chu-mo">
                so với {nhanKhoangChung(du)}
              </span>
            )}
          </h2>
          <p className="mt-1 text-phu text-chu-mo">{dongChuThich(du, sap)}</p>
        </div>

        <SegmentedControl
          aria-label="Cách xếp kênh"
          giaTri={sap}
          onChange={(v) => {
            onDoiSap(v as CachSapSoSanh);
          }}
          muc={CACH_SAP_SO_SANH.map((m) => ({ giaTri: m, nhan: CACH_SAP_NHAN[m] }))}
        />
      </div>

      {dangTai ? (
        <ul className="mt-5 flex flex-col gap-4" aria-hidden="true">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={`khung-${String(i)}`} cao="2.25rem" />
          ))}
        </ul>
      ) : loi !== null ? (
        <EmptyState loi title="Không tải được bảng so sánh" description={loi} />
      ) : items.length === 0 ? (
        <EmptyState title={nhanRong()} description={moTaRong()} />
      ) : (
        <>
          <ol className={`mt-5 ${LUOI_BANG}`}>
            {items.map((d, i) => (
              <Fragment key={d.channelId}>
                {/*
                  Vạch ngăn đứng **trước dòng chưa đủ điều kiện đầu tiên**: nó
                  chia bảng thành hai khối có nghĩa khác nhau, chứ không phải
                  một đường kẻ trang trí.
                */}
                {d.chuaDuDieuKien && !items[i - 1]?.chuaDuDieuKien && (
                  <li
                    data-vach="chua-du-dieu-kien"
                    className="col-span-5 flex items-center gap-3 pt-2 text-phu text-chu-mo"
                  >
                    <span aria-hidden="true" className="h-px flex-1 bg-vien" />
                    Chưa đủ điều kiện so sánh
                    <span aria-hidden="true" className="h-px flex-1 bg-vien" />
                  </li>
                )}

                <li data-dong="so-sanh" className={LUOI_DONG}>
                  <span className="so-lieu text-right text-phu text-chu-mo">{d.hang}</span>

                  {/* Tên kênh và Network là **một** cột: Network nằm ngay dưới tên
                    nên hai chuỗi của cùng một kênh không bị cột khác chen vào
                    giữa. Không `truncate` — xem {@link LUOI_BANG}. */}
                  <span className="min-w-0">
                    <Link
                      to={`/kenh/${d.channelId}?ky=${encodeURIComponent(maKy)}`}
                      className="block break-words text-co-ban font-semibold text-chu hover:text-nhan hover:underline"
                    >
                      {d.ten}
                    </Link>
                    <span className="block break-words text-bang text-chu-mo">
                      {d.network ?? 'AdSense trực tiếp'}
                    </span>
                  </span>

                  <span
                    className="h-2.5 overflow-hidden rounded-full bg-neutral-nen"
                    aria-hidden="true"
                  >
                    <span
                      className="block h-2.5 rounded-full bg-nhan"
                      style={{
                        width: `${String(Math.max(((d.giaTri ?? 0) / lonNhat) * 100, 1))}%`,
                      }}
                    />
                  </span>

                  <span className="whitespace-nowrap text-right">
                    <span
                      data-o="gia-tri"
                      className="so-lieu block text-co-ban font-semibold text-chu"
                    >
                      {dinhDangChiSo(d.giaTri, du?.dinhDang ?? 'so', du?.currency)}
                    </span>

                    {/*
                      Kỳ trước chỉ còn **con số**; khoảng đã ghi một lần ở tiêu
                      đề khung. Khoảng **đã cắt của chính kênh này** nằm trong
                      `title` — khác khoảng chung khi kênh thiếu ngày, và vẫn
                      cần tra được khi đối soát với Studio.
                    */}
                    {d.giaTriTruoc !== null && (
                      <span
                        data-o="ky-truoc"
                        className="so-lieu block text-bang text-chu-mo"
                        title={moTaKyTruoc(d.khoangTruoc)}
                      >
                        ← {dinhDangChiSo(d.giaTriTruoc, du?.dinhDang ?? 'so', du?.currency)}
                      </span>
                    )}
                  </span>

                  {/* Chip mức đổi có ở **cả ba** chế độ; dòng chưa đủ điều kiện
                    mang chip xám kèm lý do ngắn ngay dưới. */}
                  <span className="justify-self-end text-right">
                    {coChip(d, soTheoDiem) ? (
                      <ChipChenh
                        tang={huongChenh(d, soTheoDiem)}
                        mo={d.chuaDuDieuKien}
                        text={nhanChenh(d.delta, d.pct, soTheoDiem)}
                      />
                    ) : (
                      <span className="so-lieu block text-bang text-chu-mo">—</span>
                    )}

                    {d.chuaDuDieuKien && d.lyDo !== null && (
                      <span data-ly-do className="mt-0.5 block max-w-[9rem] text-bang text-chu-mo">
                        {d.lyDo}
                      </span>
                    )}
                  </span>
                </li>
              </Fragment>
            ))}
          </ol>

          {/* `mt-auto` đẩy nút xuống chân thẻ: ở bố cục hai cột, cột trái cao
              theo cột phải nên nút nổi lơ lửng giữa thẻ trông như bị bỏ quên. */}
          {conLai > 0 && !xemTatCa && (
            <button
              type="button"
              onClick={onXemTatCa}
              className="vung-bam mt-auto self-start rounded-full border border-vien bg-be-mat px-4 pt-4 text-co-ban font-semibold text-nhan hover:bg-neutral-nen"
            >
              Xem tất cả ({soNguyen(du?.tong ?? 0)} kênh)
            </button>
          )}
        </>
      )}
    </section>
  );
}

/** Dòng này có mức đổi in được hay không — CTR đọc `delta`, còn lại đọc `pct`. */
function coChip(d: { delta: number | null; pct: number | null }, soTheoDiem: boolean): boolean {
  return (soTheoDiem ? d.delta : d.pct) !== null;
}

/** `true` = đi lên. Đứng yên tính là lên: `▼ 0,0%` đọc ra như một mức tụt. */
function huongChenh(d: { delta: number | null; pct: number | null }, soTheoDiem: boolean): boolean {
  return ((soTheoDiem ? d.delta : d.pct) ?? 0) >= 0;
}

/**
 * Chip mức đổi: xanh/đỏ khi kênh so được, **xám** khi chưa đủ điều kiện.
 *
 * Chip xám là cách nói "con số này để tham khảo" mà vẫn in nó ra. Bản trước
 * không in gì cả, nên một kênh thiếu hai ngày số liệu trông như không có số
 * liệu nào.
 */
function ChipChenh({ tang, mo, text }: { tang: boolean; mo: boolean; text: string }) {
  const mau = mo
    ? 'bg-neutral-nen text-chu-mo'
    : tang
      ? 'bg-ok-nen text-tang'
      : 'bg-bad-nen text-giam';

  return (
    <span
      data-huong={tang ? 'tang' : 'giam'}
      data-chip={mo ? 'tham-khao' : 'xep-hang'}
      className={`so-lieu inline-block whitespace-nowrap rounded-chip px-2 py-0.5 text-bang font-semibold ${mau}`}
    >
      {tang ? '▲' : '▼'} {text}
    </span>
  );
}

/**
 * Dòng phụ nói **cách sắp đang áp** và luật lọc, vì luật đó quyết định kênh nào
 * xuống cuối bảng.
 *
 * Không nói ra thì một kênh tăng 300 % mà dưới ngưỡng rơi xuống cuối và không
 * ai biết vì sao.
 */
function dongChuThich(du: SoSanhKenhDto | undefined, sap: CachSapSoSanh): string {
  if (sap === 'cao') {
    return 'Mọi kênh trong phạm vi, xếp theo giá trị của kỳ này. Chip phần trăm chỉ để tham khảo.';
  }

  const cach = sap === 'tang' ? 'tăng nhiều nhất lên đầu' : 'giảm nhiều nhất lên đầu';
  const donVi = du?.soTheoDiem ? 'điểm phần trăm' : 'phần trăm';
  const mo = `Xếp theo mức đổi so kỳ trước (${donVi}), ${cach}.`;

  if (!du?.nguong) return mo;

  const theo =
    du.cua === 'luot-xem-truoc'
      ? 'lượt xem kỳ trước'
      : du.cua === 'luot-hien-thi-truoc'
        ? 'lượt hiển thị kỳ trước'
        : 'lượt hiển thị của cả hai kỳ';

  const soXep = du.items.filter((d) => !d.chuaDuDieuKien).length;

  return (
    `${mo} Xếp hạng ${String(soXep)}/${String(du.tong)} kênh — cần ${theo} từ ` +
    `${soNguyen(du.nguong)} trở lên và đủ ngày số liệu ở cả hai kỳ.`
  );
}

/**
 * Nhãn rỗng — chỉ còn gặp khi **phạm vi không có kênh nào**.
 *
 * Bảng liệt kê đủ kênh từ 31/10/2026, nên "không có kênh nào tăng" không còn
 * làm bảng trống: những kênh đó xuống cuối với lý do.
 */
function nhanRong(): string {
  return 'Không có kênh nào trong phạm vi này';
}

function moTaRong(): string {
  return 'Chọn một phạm vi khác ở hàng chip phía trên.';
}

/** Khoảng kỳ trước **của cả bảng**, in một lần ở tiêu đề khung. */
function nhanKhoangChung(du: SoSanhKenhDto | undefined): string | null {
  if (du === undefined || du.prevFrom === '' || du.prevTo === '') return null;
  return nhanKhoangGon({ from: du.prevFrom, to: du.prevTo });
}

/** Khoảng **đã cắt** của riêng một dòng, dành cho `title` khi đối soát. */
function moTaKyTruoc(k: { from: string; to: string } | null): string | undefined {
  return k === null ? undefined : `Kỳ trước: ${nhanKhoangGon(k)}`;
}

/**
 * `01/09 – 27/09` — khoảng kỳ trước, bỏ năm cho đỡ dài.
 *
 * Năm nằm ở dòng phụ đầu trang rồi; nhắc lại trong bảng là một con số không
 * thêm gì.
 */
function nhanKhoangGon(k: { from: string; to: string }): string {
  const ngay = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
  return `${ngay(k.from)} – ${ngay(k.to)}`;
}
